import React, { PureComponent } from 'react';
import ButtonsGroup from 'components/ui/buttonsGroup/ButtonsGroup';
import './pagination.scss';
import Button from 'components/ui/button';
import CustomScrollbar from 'components/ui/customScrollbar';
import OperationItem from 'components/paymentCard/operationItem/OperationItem';
import { addTranslation } from 'decorators/addTranslation';
import { OperationItemType } from 'components/paymentCard/operationItem/types';
import { AnyObject } from 'types/Common';

interface Props {
  operations: [];
  itemsPerPage: number;
  maxVisiblePages: number;
  operationsContainerClass?: string;
  operationItemType?: OperationItemType;
  onSelect: (operationId: string) => void;
  isCurrent?: (operation: AnyObject, index: number) => boolean;
}

interface State {
  currentPage: number;
}

const BUTTON_PREV = 'prev';
const BUTTON_NEXT = 'next';

class Pagination extends PureComponent<Props, State> {
  static defaultProps = {
    itemsPerPage: 5,
    maxVisiblePages: 5,
  };

  scrollbarRef = React.createRef<CustomScrollbar>();

  constructor(props) {
    super(props);

    this.state = {
      currentPage: 1,
    };
  }

  render() {
    const { operations, itemsPerPage } = this.props;

    const startIndex = (this.state.currentPage - 1) * itemsPerPage,
      endIndex = startIndex + itemsPerPage,
      operationBatch = operations.slice(startIndex, endIndex);

    return (
      <>
        {this.renderOperations(operationBatch)}
        {this.renderButtons()}
      </>
    );
  }

  renderOperations = (operationBatch: Array<object>) => {
    const { onSelect, operationsContainerClass, operationItemType } =
      this.props;

    return (
      <div className={operationsContainerClass}>
        <CustomScrollbar ref={this.scrollbarRef}>
          {operationBatch.map((operation, index) => {
            return (
              <OperationItem
                key={index}
                data={operation}
                isCurrent={this.isCurrentOperation(operation, index)}
                onSelect={onSelect}
                type={operationItemType}
                customClass='pagination-item'
              />
            );
          })}
        </CustomScrollbar>
      </div>
    );
  };

  renderButtons = () => {
    const pageNumbers = this.getPageNumbers(),
      lastPage = pageNumbers[pageNumbers.length - 1];

    return (
      <div className='card__footer card__footer_bordered'>
        <ButtonsGroup
          activeButtons={[this.state.currentPage.toString()]}
          separate={true}
          className='pagination'
          onClick={this.onPageChange}>
          <Button
            status='light'
            id={BUTTON_PREV}
            text='&laquo;'
            disabled={this.state.currentPage === 1}
          />

          {pageNumbers.map((page) => (
            <Button
              key={page}
              status='light'
              id={page.toString()}
              text={page.toString()}
            />
          ))}

          <Button
            status='light'
            id={BUTTON_NEXT}
            text='&raquo;'
            disabled={this.state.currentPage === lastPage}
          />
        </ButtonsGroup>
      </div>
    );
  };

  isCurrentOperation = (operation: AnyObject, index: number): boolean => {
    const { isCurrent } = this.props;

    if (isCurrent) {
      return isCurrent(operation, index);
    }

    return false;
  };

  getPageNumbers = () => {
    const { operations, itemsPerPage, maxVisiblePages } = this.props;

    const totalPages = Math.ceil(operations.length / itemsPerPage);
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(this.state.currentPage - half, 1);
    let end = Math.min(start + maxVisiblePages - 1, totalPages);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(end - maxVisiblePages + 1, 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  onPageChange = (newStateButtons: string[]) => {
    if (newStateButtons.length === 0) {
      return;
    }

    const buttonId = newStateButtons[0];

    let newPage: number;
    if (buttonId === BUTTON_PREV) {
      newPage = this.state.currentPage - 1;
    } else if (buttonId === BUTTON_NEXT) {
      newPage = this.state.currentPage + 1;
    } else {
      newPage = parseInt(buttonId);
    }

    this.setState({
      currentPage: newPage,
    });

    if (this.scrollbarRef.current) {
      this.scrollbarRef.current.scrollToTop();
    }
  };
}

export default addTranslation(Pagination);
